import { LIMIT_KEYS, type LimitKey, type Limits } from '@platform/contracts';
import {
  BadgeCheck,
  Building2,
  HardDrive,
  Plug,
  ScanSearch,
  Send,
  Sparkles,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';

/**
 * Límites de un plan en lenguaje de persona: "1 empresa", "1.500 leads/mes", "1 GB de
 * almacenamiento". `null` = ilimitado; la clave ausente no se muestra.
 */
// `always`: en es-ES el separador de miles no se pone en cifras de 4 dígitos (1500 → 1.500).
const nf = new Intl.NumberFormat('es-ES', { useGrouping: 'always' });
const eur = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const plural = (n: number, one: string, many: string) => `${nf.format(n)} ${n === 1 ? one : many}`;

function storage(mb: number): string {
  if (mb >= 1000) {
    const gb = mb / 1000;
    return `${Number.isInteger(gb) ? nf.format(gb) : gb.toLocaleString('es-ES', { maximumFractionDigits: 1 })} GB de almacenamiento`;
  }
  return `${nf.format(mb)} MB de almacenamiento`;
}

export interface LimitDef {
  icon: LucideIcon;
  /** Etiqueta del campo en el formulario de edición. */
  field: string;
  /** Unidad del campo (para el formulario): lo que escribe la persona. */
  unit?: string;
  /** El formulario trabaja en euros; el contrato guarda céntimos. */
  cents?: boolean;
  text: (n: number) => string;
  unlimited: string;
}

export const LIMIT_DEFS: Record<LimitKey, LimitDef> = {
  max_companies: {
    icon: Building2,
    field: 'Empresas',
    text: (n) => plural(n, 'empresa', 'empresas'),
    unlimited: 'Empresas ilimitadas',
  },
  max_users: {
    icon: UsersRound,
    field: 'Usuarios',
    text: (n) => plural(n, 'usuario', 'usuarios'),
    unlimited: 'Usuarios ilimitados',
  },
  max_instantly_connections: {
    icon: Plug,
    field: 'Cuentas de Instantly',
    text: (n) => plural(n, 'cuenta de Instantly', 'cuentas de Instantly'),
    unlimited: 'Cuentas de Instantly ilimitadas',
  },
  leads_ingested_per_month: {
    icon: ScanSearch,
    field: 'Leads encontrados',
    unit: 'por mes',
    text: (n) => `${nf.format(n)} leads/mes`,
    unlimited: 'Leads ilimitados',
  },
  leads_qualified_per_month: {
    icon: BadgeCheck,
    field: 'Leads cualificados',
    unit: 'por mes',
    text: (n) => `${nf.format(n)} leads cualificados/mes`,
    unlimited: 'Cualificación ilimitada',
  },
  emails_sent_per_month: {
    icon: Send,
    field: 'Emails enviados',
    unit: 'por mes',
    text: (n) => `${nf.format(n)} emails/mes`,
    unlimited: 'Emails ilimitados',
  },
  ai_budget_cents_per_month: {
    icon: Sparkles,
    field: 'Presupuesto de IA',
    unit: '€ por mes',
    cents: true,
    text: (n) => `${eur.format(n / 100)} de IA/mes`,
    unlimited: 'IA sin tope de gasto',
  },
  storage_mb: {
    icon: HardDrive,
    field: 'Almacenamiento',
    unit: 'MB',
    text: storage,
    unlimited: 'Almacenamiento ilimitado',
  },
};

export interface LimitLine {
  key: LimitKey;
  icon: LucideIcon;
  text: string;
  unlimited: boolean;
}

/** Límites presentes, en el orden canónico, ya en texto. */
export function limitLines(
  limits: Limits | Record<string, number | null | undefined>,
): LimitLine[] {
  const l = limits as Limits;
  return LIMIT_KEYS.flatMap((key) => {
    const v = l[key];
    if (v === undefined) return [];
    const def = LIMIT_DEFS[key];
    return [
      {
        key,
        icon: def.icon,
        text: v === null ? def.unlimited : def.text(v),
        unlimited: v === null,
      },
    ];
  });
}

/** Para add-ons: "+1.000 leads/mes". */
export function deltaText(limits: Limits | Record<string, number | null | undefined>): string {
  const lines = limitLines(limits);
  return lines.length ? lines.map((l) => (l.unlimited ? l.text : `+${l.text}`)).join(' · ') : '—';
}

/** Funciones de un plan (claves conocidas en lenguaje llano; el resto, humanizadas). */
export const FEATURE_LABELS: Record<string, string> = {
  inbound_bot: 'Asistente que responde solo',
  sheet_export: 'Exportación a Google Sheets',
  client_prompt_edit: 'El cliente edita sus instrucciones',
  gmail_dossier: 'Dossier enviado por Gmail',
};

export function featureLabel(key: string): string {
  const known = FEATURE_LABELS[key];
  if (known) return known;
  const text = key.replace(/[._-]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Lee los campos de límites del formulario (`limit_<clave>` + `unl_<clave>`). */
export function readLimits(form: FormData): Limits {
  const out: Limits = {};
  for (const key of LIMIT_KEYS) {
    if (form.get(`unl_${key}`) === 'on') {
      out[key] = null;
      continue;
    }
    const raw = form.get(`limit_${key}`);
    if (typeof raw !== 'string' || raw.trim() === '') continue;
    const n = Number(raw.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) throw new Error(`${LIMIT_DEFS[key].field}: número inválido`);
    out[key] = Math.round(LIMIT_DEFS[key].cents ? n * 100 : n);
  }
  return out;
}
